'use client'

import * as bitcoin from 'bitcoinjs-lib'
import * as btc from '@scure/btc-signer'
import { schnorr, utils } from '@noble/secp256k1'
import { NETWORK, NETWORK_CONFIG, STORAGE_PUBLIC_KEY } from '@/utils/constants'
import { validateMnemonic } from '@/utils/mnemonic'

export { normalizeMnemonic, validateMnemonic } from '@/utils/mnemonic'

interface SignData {
	psbt: string
	disableExtract?: boolean
	skipSignIndex?: number
	/** Explicit opt-in supplied only by the offer signing adapter. */
	offerEscrowIndexes?: number[]
	/**
	 * Allow-list of inputs this call may sign. When present, every other
	 * input is left untouched even if its prevout is ours or a leaf names our
	 * key. Callers that omit it keep the historical behaviour: every owned
	 * input and every leaf naming our key is signed.
	 */
	signIndexes?: number[]
	/**
	 * Sighash types the owned-input pass may use. Omitted, it keeps the
	 * historical list (DEFAULT, ALL, 0x81, 0x83) listings rely on; the offer
	 * funding path passes [0, 1] so no funding signature can be reused.
	 */
	allowedSighashes?: number[]
	//utxos: SignUtxo[]
}

const COIN_NETWORK = (NETWORK_CONFIG as any).networkParams
const DERIVATION = (NETWORK_CONFIG as any).derivation

export async function privateKeyToKeypair(
	wif: string
): Promise<{ public_key: string; private_key: string }> {
	const { PrivateKey, PublicKey } = await import('@/utils/sdk.wasm')

	let private_key

	try {
		private_key = PrivateKey.from_wif(wif)
	} catch (_e) {
		private_key = PrivateKey.from_hex(wif)
	}

	return {
		private_key: private_key.to_hex(),
		public_key: PublicKey.from_private_key(private_key).to_hex()
	}
}

/** The public key this browser's wallet was registered with, if any. */
function storedWalletPublicKey(): string | null {
	try {
		if (typeof localStorage === 'undefined') return null
		return localStorage.getItem(STORAGE_PUBLIC_KEY)
	} catch {
		return null
	}
}

export async function seedToKeypair(
	seed: string,
	options: { exact?: boolean } = {}
): Promise<{ public_key: string; private_key: string; type: string; legacy_raw?: boolean }> {
	try {
		const res = await privateKeyToKeypair(seed)
		return { ...res, type: 'pk' }
	} catch (_e) {
		const { ExtendedPrivateKey, PublicKey } = await import('@/utils/sdk.wasm')
		const derive = (phrase: string) => {
			const xpriv = ExtendedPrivateKey.from_mnemonic(Buffer.from(phrase, 'utf8'), null as any)
			const key = xpriv.derive_from_path(DERIVATION).get_private_key()
			return { private_key: key.to_hex(), public_key: PublicKey.from_private_key(key).to_hex() }
		}
		// WASM PBKDF2s the mnemonic bytes as-is. Match validateMnemonic (NFKD,
		// lowercase, collapsed spaces) or "Abandon  abandon" derives a different
		// bc1p than the phrase the user was shown.
		const normalized = await validateMnemonic(seed)
		if (options.exact) {
			// The user asked for the bytes as typed (a wallet restored before
			// normalization existed). Checksum is still enforced above.
			return { ...derive(seed), type: 'seed', legacy_raw: normalized !== seed }
		}
		let keys = derive(normalized)
		let legacy_raw = false
		// A wallet restored with odd casing before this normalization existed
		// was derived from the raw bytes and may hold funds at that address.
		// Its registered public key tells us: if the normalized derivation is
		// not the wallet this browser registered but the raw one is, keep the
		// raw derivation rather than silently moving the user to a new key.
		if (normalized !== seed) {
			const registered = storedWalletPublicKey()
			if (registered && keys.public_key !== registered) {
				const raw = derive(seed)
				if (raw.public_key === registered) {
					keys = raw
					legacy_raw = true
				}
			}
		}
		return { ...keys, type: 'seed', legacy_raw }
	}
}

export async function publicKeyToAddress(
	public_key: string,
	type?: 'p2tr' | 'p2pkh'
): Promise<any> {
	const bip371 = await import('bitcoinjs-lib/src/psbt/bip371')
	const res = (
		{
			// @ts-ignore
			BTC: () => btc.p2tr(bip371.toXOnly(Buffer.from(public_key, 'hex')), COIN_NETWORK),
			DOGE: () => btc.p2pkh(Buffer.from(public_key, 'hex'), COIN_NETWORK),
			BELLS: () => {
				if (type === 'p2tr') {
					return btc.p2tr(bip371.toXOnly(Buffer.from(public_key, 'hex')), undefined, COIN_NETWORK)
				}
				return btc.p2pkh(Buffer.from(public_key, 'hex'), COIN_NETWORK)
			},
			SIGNET: () =>
				btc.p2tr(bip371.toXOnly(Buffer.from(public_key, 'hex')), undefined, COIN_NETWORK)
		} as any
	)[NETWORK]()
	return res
}

export async function dummyPsbt(public_key: string): Promise<string> {
	const { script, tapInternalKey } = await publicKeyToAddress(public_key)

	if (['BTC', 'SIGNET'].includes(NETWORK)) {
		const tx = new btc.Transaction({ version: 0, lockTime: 0 })
		tx.addInput({
			txid: '0000000000000000000000000000000000000000000000000000000000000000',
			sequence: 4294967295,
			index: 0,
			witnessUtxo: {
				script: script,
				// @ts-ignore
				amount: 0n
			},
			sighashType: btc.SignatureHash.SINGLE | btc.SignatureHash.ANYONECANPAY,
			tapInternalKey: tapInternalKey
		})
		tx.addOutput({
			// @ts-ignore
			amount: 0n,
			script: script
		})

		const hex = Buffer.from(tx.toPSBT()).toString('hex')
		return hex
	} else {
		const tx = new btc.Transaction({ version: 0, lockTime: 0 })
		const non_witness_tx = new btc.Transaction({ version: 0, lockTime: 0 })
		non_witness_tx.addOutput({ script, amount: BigInt(1e8) })
		non_witness_tx.addInput({
			txid: '0000000000000000000000000000000000000000000000000000000000000000',
			index: 0,
			sequence: 4294967295,
			finalScriptSig: Buffer.from('00', 'hex')
		})
		tx.addOutput({ script, amount: BigInt(1e8) })
		tx.addInput({
			txid: non_witness_tx.id,
			index: 0,
			sequence: 4294967295,
			nonWitnessUtxo: non_witness_tx.hex,
			witnessUtxo: {
				script: script,
				amount: BigInt(1e8)
			}
		})
		const psbt = Buffer.from(tx.toPSBT()).toString('hex')
		return psbt
	}
}

export async function buildDummyPSBT(pubkey: any) {
	const bip371 = await import('bitcoinjs-lib/src/psbt/bip371')
	const tx = new btc.Transaction({ version: 0, lockTime: 0 })
	let public_key = bip371.toXOnly(Buffer.from(pubkey, 'hex'))
	const script = btc.p2tr(public_key)
	tx.addInput({
		txid: '0000000000000000000000000000000000000000000000000000000000000000',
		sequence: 4294967295,
		index: 0,
		witnessUtxo: {
			script: script.script,
			// @ts-ignore
			amount: 0n
		},
		sighashType: btc.SignatureHash.SINGLE | btc.SignatureHash.ANYONECANPAY,
		tapInternalKey: script.tapInternalKey
	})
	tx.addOutput({
		// @ts-ignore
		amount: 0n,
		script: script.script
	})

	const hex = Buffer.from(tx.toPSBT()).toString('hex')
	return hex
}

export async function signMessage(seed: string, message: string): Promise<string> {
	const { Wallet } = await import('@/utils/sdk.wasm')
	const wallet = new Wallet(seed)
	const private_key = (wallet as any).taproot_xpriv().get_private_key().to_hex()
	const public_key = schnorr.getPublicKey(private_key)
	const script = btc.p2tr(public_key)

	const tx = new btc.Transaction({ version: 0, lockTime: 0 })

	let buf = new Uint8Array(34)
	buf[0] = 0
	buf[1] = 32
	buf.set(await utils.taggedHash('BIP0322-signed-message', Buffer.from(message, 'utf8')), 2)

	tx.addOutput({
		// @ts-ignore
		amount: 0n,
		script: script.script
	})
	tx.addInput({
		txid: '0000000000000000000000000000000000000000000000000000000000000000',
		index: 4294967295,
		sequence: 0,
		finalScriptSig: buf
	})

	const tx2 = new btc.Transaction({
		version: 0,
		lockTime: 0,
		allowUnknowOutput: !0
	})

	tx2.addInput({
		txid: tx.id,
		index: 0,
		sequence: 0,
		witnessUtxo: {
			script: script.script,
			// @ts-ignore
			amount: 0n
		},
		sighashType: btc.SignatureHash.ALL,
		tapInternalKey: script.tapInternalKey
	})
	tx2.addOutput({
		// @ts-ignore
		amount: 0n,
		script: Uint8Array.from([btc.OP.RETURN])
	})
	tx2.signIdx(
		Buffer.from(private_key, 'hex'),
		0,
		[btc.SignatureHash.ALL],
		new Uint8Array(32).fill(0)
	)
	tx2.finalizeIdx(0)

	return Buffer.concat([
		Buffer.from(Uint8Array.from([1, 65])),
		// @ts-ignore
		Buffer.from(tx2.inputs[0].finalScriptWitness[0])
	]).toString('base64')

	//return tx2.inputs[0].finalScriptWitness
}

export async function exportKeys(seed: string) {
	const [{ PrivateKey }, { private_key }] = await Promise.all([
		import('@/utils/sdk.wasm'),
		seedToKeypair(seed)
	])

	const private_key_hex = private_key
	const private_key_wif = PrivateKey.from_hex(private_key).to_wif()

	return { private_key_hex, private_key_wif, derivation: DERIVATION }
}

const findScript = (input: any) => {
	if (input?.witnessUtxo) {
		return Buffer.from(input?.witnessUtxo?.script).toString('hex')
	}

	if (input?.nonWitnessUtxo) {
		return Buffer.from(input?.nonWitnessUtxo?.outputs?.[input.index]?.script || '00').toString(
			'hex'
		)
	}
}

export /**
 * True when a PSBT input is a Taproot script-path spend whose leaf names our
 * x-only key (passthrough v4 sale template). Its prevout is the escrow, not
 * our address, so the local-script test would otherwise skip it.
 */
const hasOurLeaf = (input: any, xonlyHex: string): boolean => {
	const leafs = input?.tapLeafScript
	if (!Array.isArray(leafs)) return false
	return leafs.some((entry: any) => {
		const script = Array.isArray(entry) ? entry[1] : entry?.script
		return script ? Buffer.from(script).toString('hex').includes(xonlyHex) : false
	})
}

export const signRawtx = async (seed: string, data: SignData) => {
	const { private_key, public_key: publ } = await seedToKeypair(seed)
	const ourXOnlyHex = Buffer.from(schnorr.getPublicKey(private_key)).toString('hex')

	let tx

	try {
		tx = btc.Transaction.fromPSBT(Buffer.from(data.psbt, 'hex'), {
			allowLegacyWitnessUtxo: true
		})
	} catch (_e) {
		// @ts-ignore
		const network = bitcoin.networks.mainnet
		const temp = bitcoin.Psbt.fromHex(data.psbt, { network })
		tx = btc.Transaction.fromPSBT(Buffer.from(temp.toHex(), 'hex'), {
			allowLegacyWitnessUtxo: true
		})
	}

	const { script } = await publicKeyToAddress(publ)
	const scriptHex = Buffer.from(script).toString('hex')
	const allowed = (i: number) => !data.signIndexes || data.signIndexes.includes(i)

	// @ts-ignore
	tx.inputs.map((e, i) => {
		const isLocalScript = scriptHex === findScript(e)
		const skipSignIndex = data.skipSignIndex === i || !isLocalScript || !allowed(i)

		if (skipSignIndex) {
			return
		}

		const isP2tr =
			typeof scriptHex === 'string' && scriptHex.startsWith('5120') && scriptHex.length === 68
		if (['BTC', 'SIGNET'].includes(NETWORK) && isP2tr) {
			// Stamp Schnorr metadata only on Taproot inputs. ECDSA (P2WPKH /
			// nested P2SH-P2WPKH) listings must not receive tapInternalKey.
			// @ts-ignore
			tx.inputs[i].tapInternalKey = schnorr.getPublicKey(private_key)

			// @ts-ignore
			if (tx.inputs[i].nonWitnessUtxo) {
				// @ts-ignore
				delete tx.inputs[i].nonWitnessUtxo
			}
		}
	})

	// @ts-ignore
	tx.inputs.map((e, i) => {
		const isLocalScript = scriptHex === findScript(e)
		const skipSignIndex = data.skipSignIndex === i || !isLocalScript || !allowed(i)

		if (skipSignIndex) {
			return
		}

		// @ts-ignore
		if (tx.inputs[i].tapBip32Derivation) {
			// @ts-ignore
			delete tx.inputs[i].tapBip32Derivation
		}

		try {
			// 129 = SIGHASH_ALL|ANYONECANPAY (0x81) used by Build 3 listings.
			// Legacy 0x83 (131) and SIGHASH_ALL (1) paths are unchanged.
			tx.signIdx(
				Buffer.from(private_key, 'hex'),
				i,
				(data.allowedSighashes ?? [0, 1, 129, 131]) as any
			)
		} catch (e) {
			// A caller with an explicit allow-list gets a clear refusal rather
			// than an input left unsigned for the finalizer to trip over.
			if (data.allowedSighashes)
				throw new Error(`Input ${i} asks for a signature type this request does not allow. Nothing was signed.`)
			console.log(e)
		}
	})
	// Protected listings and offers: a script-path input of our own key whose
	// prevout is the escrow. Sign the leaf with the untweaked key and leave the
	// input unfinalized; Ordinals Wallet adds its co-signature at settlement.
	// @ts-ignore
	tx.inputs.map((e, i) => {
		if (
			data.skipSignIndex === i ||
			!allowed(i) ||
			scriptHex === findScript(e) ||
			!hasOurLeaf(e, ourXOnlyHex)
		) {
			return
		}
		try {
			// ALL pins item offers/refunds; NONE|ANYONECANPAY authorizes
			// collection fills; SINGLE|ANYONECANPAY authorizes protected sales.
			tx.signIdx(Buffer.from(private_key, 'hex'), i,
				data.offerEscrowIndexes?.includes(i) ? [1, 130 as any] : [131 as any])
		} catch (e) {
			console.log(e)
		}
	})

	// @ts-ignore
	tx.inputs.map((e, i) => {
		const isLocalScript = scriptHex === findScript(e)
		const skipSignIndex = data.skipSignIndex === i || !isLocalScript || !allowed(i)

		if (skipSignIndex) {
			return
		}

		//try {
		//tx.finalizeIdx(i)

		//} catch (e) {
		//
		const isP2tr =
			typeof scriptHex === 'string' && scriptHex.startsWith('5120') && scriptHex.length === 68
		if (['BTC', 'SIGNET'].includes(NETWORK) && isP2tr) {
			// @ts-ignore
			tx.inputs[i].finalScriptWitness = [tx.inputs[i].tapKeySig]
		}

		if (['BELLS', 'DOGE'].includes(NETWORK)) {
			// @ts-ignore
			tx.inputs[i].finalScriptSig = btc.Script.encode([
				// @ts-ignore
				tx.inputs[i].partialSig[0][1],
				// @ts-ignore
				tx.inputs[i].partialSig[0][0]
			])

			// @ts-ignore
			cleanFinalInput(tx.inputs[i])
			//}
		}
	})

	if (data.disableExtract) {
		return Buffer.from(tx.toPSBT()).toString('hex')
	}

	return tx.hex
}

const PSBT_INPUT_FINAL_KEYS = new Set([
	'txid',
	'sequence',
	'index',
	'witnessUtxo',
	'nonWitnessUtxo',
	'finalScriptSig',
	'finalScriptWitness',
	'unknown'
])

function cleanFinalInput(i: any) {
	for (const _k in i) {
		const k = _k
		if (!PSBT_INPUT_FINAL_KEYS.has(k)) delete i[k]
	}
}

/**
 * Runtime entropy self-test, run before we hand a user a seed.
 *
 * The COLDCARD RNG failure (July 2026) shipped for five years because a
 * build-time symbol silently selected a weak PRNG and *nothing ever checked at
 * runtime* that the strong path had been taken. bitcore-lib had the same shape
 * here — it branched on `process.browser`, a webpack-4 global — which is why
 * this file no longer uses it. These assertions are the backstop: they cost
 * microseconds and they fail closed, so a wallet is never issued from an RNG
 * we could not verify.
 */
function assertSecureEntropy(): void {
	const c = globalThis.crypto
	if (!c || typeof c.getRandomValues !== 'function') {
		throw new Error(
			'Refusing to generate a wallet: no secure RNG (crypto.getRandomValues) available.'
		)
	}

	// A stuck or constant generator is the failure mode that silently yields
	// duplicate or low-entropy seeds, so require two independent draws to differ
	// and reject an obviously degenerate one.
	const a = c.getRandomValues(new Uint8Array(32))
	const b = c.getRandomValues(new Uint8Array(32))
	if (a.length === b.length && a.every((v, i) => v === b[i])) {
		throw new Error('Refusing to generate a wallet: RNG returned identical consecutive outputs.')
	}
	if (a.every((v) => v === a[0])) {
		throw new Error('Refusing to generate a wallet: RNG returned a constant byte pattern.')
	}
}

const bip39 = async () => {
	const [lib, english] = await Promise.all([
		import('@scure/bip39'),
		import('@scure/bip39/wordlists/english')
	])
	return { lib, wordlist: english.wordlist }
}

export const generateMnemonic = async (): Promise<string> => {
	assertSecureEntropy()
	const { lib, wordlist } = await bip39()
	// 128 bits -> 12 words, identical to what this wallet has always issued.
	// @scure/bip39 draws from crypto.getRandomValues and throws if it is
	// unavailable; there is no weak fallback to land on.
	return lib.generateMnemonic(wordlist, 128)
}
